import { useMemo, useState, type Ref } from 'react';
import { Check, Search } from 'lucide-react';
import { Checkbox } from '@/components/ui/Checkbox';
import { Input } from '@/components/ui/Input';
import { practitionerPickRows, type PractitionerPickRow } from '@/lib/practitionerSuggestions';
import { TYPE } from '@/lib/typography';
import { cn } from '@/utils/cn';

/**
 * Searchable behandler picker.
 *
 * Same shape as Værktøjer → Spørgeskemaer: the box is empty until the user
 * searches, and a short labelled group (Foreslåede behandlere) stands in for
 * «Tilladte på dette trin». Already selected people stay visible.
 *
 * Bloker tid passes `showEveryoneWhenIdle` so the clinic roster stays in the
 * scrollable list until a search narrows it. Ny aftale and forløb do not.
 */
export function PractitionerSearchList({
  people,
  suggestedIds,
  selectedIds,
  onChange,
  legend,
  suggestionLabel,
  searchPlaceholder,
  emptyLabel,
  hint,
  embedded = false,
  tone = 'field',
  showEveryoneWhenIdle = false,
  disabled = false,
  inputRef,
}: {
  people: readonly PractitionerPickRow[];
  suggestedIds: readonly string[];
  selectedIds: readonly string[];
  onChange: (ids: string[]) => void;
  legend: string;
  suggestionLabel: string;
  searchPlaceholder: string;
  emptyLabel: string;
  hint?: string;
  /** Skip the fieldset so a parent legend can own the control. */
  embedded?: boolean;
  /** `tools` uses the same rows as Værktøjer → Spørgeskemaer. */
  tone?: 'field' | 'tools';
  /** Empty search lists every person. Booking suggestions leave this off. */
  showEveryoneWhenIdle?: boolean;
  disabled?: boolean;
  inputRef?: Ref<HTMLInputElement>;
}) {
  const [query, setQuery] = useState('');
  const { rows, showSuggestions } = useMemo(
    () =>
      practitionerPickRows({
        query,
        people,
        suggestedIds,
        selectedIds,
        showEveryoneWhenIdle,
      }),
    [people, query, selectedIds, showEveryoneWhenIdle, suggestedIds],
  );
  const selected = useMemo(() => new Set(selectedIds), [selectedIds]);
  const tools = tone === 'tools';
  const Root = embedded ? 'div' : 'fieldset';

  function toggle(id: string, checked: boolean) {
    if (disabled) return;
    if (checked) {
      if (selected.has(id)) return;
      onChange([...selectedIds, id]);
      return;
    }
    onChange(selectedIds.filter((value) => value !== id));
  }

  return (
    <Root data-testid="practitioner-search-list">
      {embedded ? null : (
        <legend className={cn(TYPE.small, 'mb-s2 font-medium text-ds-text')}>{legend}</legend>
      )}
      <Input
        ref={inputRef}
        type="search"
        value={query}
        onChange={(event) => setQuery(event.target.value)}
        placeholder={searchPlaceholder}
        aria-label={searchPlaceholder}
        autoComplete="off"
        disabled={disabled}
        leadingIcon={<Search className="h-4 w-4" aria-hidden />}
      />
      {showSuggestions ? (
        <p
          className={
            tools
              ? 'mt-3 text-xs font-semibold text-ds-muted'
              : cn(TYPE.meta, 'mb-s1 mt-s3 text-ds-muted')
          }
          data-testid="practitioner-suggestions"
        >
          {suggestionLabel}
        </p>
      ) : null}
      {rows.length > 0 ? (
        <ul
          className={cn(
            'max-h-60 overflow-y-auto',
            tools ? 'mt-2 space-y-1' : showSuggestions ? 'mt-s1' : 'mt-s2',
          )}
        >
          {rows.map((person) => {
            const checked = selected.has(person.id);
            return (
              <li key={person.id}>
                {tools ? (
                  <label className={cn('qp-r', checked && 'on', disabled && 'dis')}>
                    <input
                      type="checkbox"
                      className="sr-only"
                      checked={checked}
                      disabled={disabled}
                      onChange={(event) => toggle(person.id, event.target.checked)}
                    />
                    <span className="box" aria-hidden>
                      {checked ? <Check className="h-3.5 w-3.5" strokeWidth={1.75} /> : null}
                    </span>
                    <span>
                      <span className="nm">{person.name}</span>
                    </span>
                  </label>
                ) : (
                  <Checkbox
                    checked={checked}
                    disabled={disabled}
                    onChange={(event) => toggle(person.id, event.target.checked)}
                    label={person.name}
                  />
                )}
              </li>
            );
          })}
        </ul>
      ) : query.trim() ? (
        <p
          className={cn(TYPE.small, 'mt-s2 text-ds-muted')}
          data-testid="practitioner-search-empty"
        >
          {emptyLabel}
        </p>
      ) : null}
      {hint ? <p className={cn('mt-s2', TYPE.small, 'text-ds-muted')}>{hint}</p> : null}
    </Root>
  );
}
